import React, { useState, useEffect } from 'react';
import LoadingScreen from '../components/ui/LoadingScreen';
import { useParams, useNavigate } from 'react-router-dom';
import { Music2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const GuestJoin = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    // Check if already joined
    const savedSession = localStorage.getItem('guestSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.eventId === eventId && new Date(session.expiresAt) > new Date()) {
          // Session still valid, redirect to dashboard
          navigate(`/guest/${eventId}/dashboard`);
          return;
        } else {
          // Session expired or wrong event, clear it
          localStorage.removeItem('guestSession');
        }
      } catch (e) {
        localStorage.removeItem('guestSession');
      }
    }

    // Load event info
    loadEvent();
  }, [eventId, navigate]);

  const loadEvent = async () => {
    try {
      // Public endpoint to get basic event info (no auth)
      const response = await api.get(`/events/${eventId}/public`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error loading event:', error);
      toast.error('Événement introuvable');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    
    if (!guestName.trim()) {
      toast.error('Veuillez entrer votre nom');
      return;
    }

    setJoining(true);
    try {
      const response = await api.post('/guests/join', {
        event_id: eventId,
        name: guestName.trim()
      });

      const guest = response.data;
      
      // Save session to localStorage
      const session = {
        guestId: guest.id,
        guestName: guest.name,
        eventId: guest.event_id,
        expiresAt: guest.expires_at,
        suggestionsCount: guest.suggestions_count
      };
      localStorage.setItem('guestSession', JSON.stringify(session));

      toast.success(`Bienvenue ${guest.name} ! 👋`);
      navigate(`/guest/${eventId}/dashboard`);
    } catch (error) {
      console.error('Error joining:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la connexion');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Recherche de l'événement..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-gradient-to-br from-orange-500 to-coral-500 rounded-2xl mb-4 shadow-lg">
            <Music2 className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">DJ Nomad</h1>
          <p className="text-slate-400">Rejoignez l'événement</p>
        </div>

        {/* Event Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-8 shadow-xl">
          {event ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">{event.name}</h2>
                <p className="text-slate-400">{event.date}</p>
                {event.location && (
                  <p className="text-slate-500 text-sm mt-1">📍 {event.location}</p>
                )}
              </div>

              <form onSubmit={handleJoin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Votre nom
                  </label>
                  <input
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Ex: Jean Dupont"
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                    maxLength={50}
                    disabled={joining}
                  />
                </div>

                <button
                  type="submit"
                  disabled={joining || !guestName.trim()}
                  className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-coral-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-coral-600 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg hover:shadow-orange-500/25"
                >
                  {joining ? 'Connexion...' : 'Rejoindre l\'événement 🎵'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center text-slate-400">
              <p>Événement introuvable</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Suggérez vos pistes favorites au DJ ! 🎧
        </p>
      </div>
    </div>
  );
};

export default GuestJoin;
